
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface IndustryRisk {
  id: string;
  risk_name: string;
  risk_description: string;
  mitigation_strategy: string;
  risk_category: string;
  industry_category: string;
  industry_subcategory: string;
  industry_tags: string[];
  likelihood: number;
  impact: number;
}

interface IndustryRiskSearchProps {
  onSelectRisk: (risk: IndustryRisk) => void;
}

export const IndustryRiskSearch = ({ onSelectRisk }: IndustryRiskSearchProps) => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [risks, setRisks] = useState<IndustryRisk[]>([]);
  const [filteredRisks, setFilteredRisks] = useState<IndustryRisk[]>([]);
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState<string[]>([]);
  const [categories] = useState(['climate', 'regulatory', 'operational', 'financial', 'reputational']);

  useEffect(() => {
    fetchRisks();
  }, []);

  useEffect(() => {
    filterRisks();
  }, [searchTerm, selectedCategory, selectedIndustry, risks]);

  const fetchRisks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('industry_risks')
        .select('*')
        .order('industry_category', { ascending: true });

      if (error) throw error;

      setRisks(data || []);
      
      // Extract unique industries
      const uniqueIndustries = [...new Set(data?.map(risk => risk.industry_category) || [])];
      setIndustries(uniqueIndustries);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch industry risks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterRisks = () => {
    let filtered = risks;

    if (searchTerm) {
      filtered = filtered.filter(risk =>
        risk.risk_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        risk.risk_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        risk.industry_tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(risk => risk.risk_category === selectedCategory);
    }

    if (selectedIndustry !== 'all') {
      filtered = filtered.filter(risk => risk.industry_category === selectedIndustry);
    }

    setFilteredRisks(filtered);
  };

  const getRiskScoreColor = (likelihood: number, impact: number) => {
    const score = likelihood * impact;
    if (score >= 20) return 'bg-red-500';
    if (score >= 15) return 'bg-orange-500';
    if (score >= 10) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Industry Risk Database
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-2">
          Browse industry-specific risks from our database. Click "Add to My Assessments" to copy any risk to your personal risk assessments where you can customize and manage it.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search risks by name, description, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Risk Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map(industry => (
                <SelectItem key={industry} value={industry}>
                  {industry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">Loading risks...</div>
          ) : filteredRisks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No risks found matching your criteria
            </div>
          ) : (
            filteredRisks.map((risk) => (
              <Card key={risk.id} className="cursor-pointer hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{risk.risk_name}</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        {risk.industry_category} - {risk.industry_subcategory}
                      </p>
                      <p className="text-sm mb-3">{risk.risk_description}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="outline">{risk.risk_category}</Badge>
                        {risk.industry_tags.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-4 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Risk Score:</span>
                          <div className={`w-6 h-6 rounded-full ${getRiskScoreColor(risk.likelihood, risk.impact)} flex items-center justify-center text-white text-xs font-bold`}>
                            {risk.likelihood * risk.impact}
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          Likelihood: {risk.likelihood}/5 | Impact: {risk.impact}/5
                        </span>
                      </div>
                      
                      <div className="text-sm">
                        <span className="font-medium">Mitigation: </span>
                        <span className="text-muted-foreground">{risk.mitigation_strategy}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSelectRisk(risk)}
                      className="ml-4 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to My Assessments
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
